import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupervisorFindingDismissReason } from '@avisoma/shared';
import { useToast } from '../../components/Toast';
import { findingMutationErrorToast, findingMutationToast, type FindingActionKind } from '../../lib/findingMutationToast';
import { createSingleFlight } from '../../lib/singleFlight';
import { invalidateSupervisorQueries } from '../../lib/supervisorQueries';
import backend from '../../services/backend';

type FindingAction =
  | { kind: Exclude<FindingActionKind, 'dismiss'>; findingId: string }
  | { kind: 'dismiss'; findingId: string; reason: SupervisorFindingDismissReason };

/** The project travels with the action, so a settle after a project switch invalidates the right one. */
type FindingMutationVars = FindingAction & { projectId: string };

export type SupervisorFindingMutations = {
  apply: (findingId: string) => void;
  revert: (findingId: string) => void;
  dismiss: (findingId: string, reason: SupervisorFindingDismissReason) => void;
  restore: (findingId: string) => void;
  /** True while any action runs; the tab disables every action button until it settles. */
  busy: boolean;
};

const send = (v: FindingMutationVars) => {
  switch (v.kind) {
    case 'apply':
      return backend.applySupervisorFinding(v.projectId, v.findingId);
    case 'revert':
      return backend.revertSupervisorFinding(v.projectId, v.findingId);
    case 'restore':
      return backend.restoreSupervisorFinding(v.projectId, v.findingId);
    case 'dismiss':
      return backend.dismissSupervisorFinding(v.projectId, v.findingId, { reason: v.reason });
  }
};

/**
 * Apply / revert / dismiss / restore for the Corrections tab, one action at a time. No optimistic
 * update: the rows change when the refetch lands. Failed requests are toasted here (the backend calls
 * opt out of the interceptor's generic toast), a 409 `invalid_transition` as "Finding changed, list
 * refreshed"; the refetch in `onSettled` shows the finding's current status.
 */
export function useSupervisorFindingMutations(projectId: string): SupervisorFindingMutations {
  const queryClient = useQueryClient();
  const toast = useToast();
  // A second click can land before `isPending` re-renders the buttons disabled.
  const [flight] = useState(createSingleFlight);
  const mutation = useMutation({
    mutationFn: async (v: FindingMutationVars) => (await send(v)).data,
    onSuccess: (finding, v) => {
      const t = findingMutationToast(v.kind, finding);
      toast.show(t.message, t.type);
    },
    onError: (error) => {
      const t = findingMutationErrorToast(error);
      toast.show(t.message, t.type);
    },
    // Awaited: `isPending` holds until the refetch lands, so the buttons stay busy until the rows change.
    onSettled: async (_data, _error, v) => {
      await invalidateSupervisorQueries(queryClient, v.projectId);
      flight.settle();
    },
  });
  const run = (action: FindingAction) => {
    if (flight.start()) mutation.mutate({ ...action, projectId });
  };
  return {
    apply: (findingId) => run({ kind: 'apply', findingId }),
    revert: (findingId) => run({ kind: 'revert', findingId }),
    dismiss: (findingId, reason) => run({ kind: 'dismiss', findingId, reason }),
    restore: (findingId) => run({ kind: 'restore', findingId }),
    busy: mutation.isPending,
  };
}
